import { View, Text, StyleSheet, TextInput, Linking } from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { Feather, Ionicons } from "@expo/vector-icons";
import { getAuth, signOut } from "firebase/auth";
import { useScreenDimensions } from "../_layout";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import CustomAlert from "../Components/CustomAlert";
import TypedConfirmationModal from "../Components/TypedConfirmationModal";
import Tooltip from "../Components/Tooltip";
import db from "../Firestore/FirestoreConfig";
import { storage } from "../Firestore/FirestoreConfig";
import { doc, updateDoc, deleteDoc, getDoc } from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import LogoImage from "../Components/LogoImage";
import { getHeaderLogoMaxWidth } from "../Components/logoImageHeaderSizing";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import NoTranslateText from "../Components/NoTranslateText";
import { getFormattedVersion } from "../utils/version";
import BlockViewRevertModal from "../Block/shared/BlockViewRevertModal";
import HoverableTouchableOpacity from "../Components/HoverableTouchableOpacity";
import ProfileSegmentedControl from "../Components/ProfileSegmentedControl";
import { logoutCoachSession } from "../auth/logoutCoachSession";
import {
  discardCoachSettingsDraft,
  hasCoachSettingsUnsavedChanges,
  syncCoachSettingsDraftWithPersistedData,
} from "../platform/guards/coachSettingsChanges";
import { shouldShowNutritionFromSidebarConfig } from "../nutrition/nutritionSidebarVisibility";

const MAX_SIZE_IN_BYTES = 5 * 1024 * 1024;
const STRIPE_BILLING_URL =
  "https://billing.stripe.com/p/login/9AQ3fW6andcNgne000";
const INSTAGRAM_URL = "https://www.instagram.com/efortapp/";
const FILE_INPUT_ID = "coach-settings-logo-input";

const normalizeTypedConfirmationValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const matchesTypedConfirmationValue = ({ input, expectedValue }) => {
  const normalizedExpectedValue =
    normalizeTypedConfirmationValue(expectedValue);
  if (!normalizedExpectedValue) return false;
  return normalizeTypedConfirmationValue(input) === normalizedExpectedValue;
};

const ALERT_CONFIGS = {
  resetExercises: {
    title: "resetExercisesAlertTitle-string",
    text: "resetExercisesAlertText-string",
    cta: "resetButton-string",
  },
  resetAttributes: {
    title: "resetAttributesAlertTitle-string",
    text: "resetAttributesAlertText-string",
    cta: "resetButton-string",
  },
  deleteAccount: {
    title: "deleteAccountAlertTitle-string",
    text: "deleteAccountAlertText-string",
    cta: "delete-string",
  },
  deleteLogo: {
    title: "deleteLogoAlertTitle-string",
    text: "deleteLogoAlertText-string",
    cta: "delete-string",
  },
  uploadError: {
    title: "uploadErrorTitle-string",
    cta: "change-string",
  },
};

const Section = ({ title, children, topMargin = true, screenSize }) => (
  <View
    style={{
      marginTop: topMargin ? (screenSize === "small" ? 20 : 28) : 0,
    }}
  >
    <Text style={[Fonts.medium20, styles.sectionTitle]}>{title}</Text>
    <View style={styles.settingsCard}>{children}</View>
  </View>
);

const Separator = () => <View style={styles.separator} />;

const SettingsRow = ({
  label,
  children,
  screenSize,
  hasDescription = false,
  description,
  tooltip,
  noTranslateDescription = false,
  isLast = false,
}) => {
  const isSmall = screenSize === "small";

  return (
    <>
      <View style={[styles.row, isSmall && styles.rowSmall]}>
        <View
          style={[
            styles.rowLabelContainer,
            isSmall ? styles.rowLabelContainerSmall : null,
          ]}
        >
          <View style={styles.rowLabelLine}>
            <Text style={[Fonts.regular14, styles.rowLabel]}>{label}</Text>
            {tooltip && (
              <Tooltip content={tooltip} placement="top" maxWidth={300}>
                <View style={styles.tooltipIconContainer}>
                  <Ionicons
                    name="information-circle-outline"
                    size={18}
                    color={Colors.grayColor}
                  />
                </View>
              </Tooltip>
            )}
          </View>

          {hasDescription &&
            (noTranslateDescription ? (
              <NoTranslateText style={[Fonts.regular13, styles.rowDescription]}>
                {description}
              </NoTranslateText>
            ) : (
              <Text style={[Fonts.regular13, styles.rowDescription]}>
                {description}
              </Text>
            ))}
        </View>

        <View
          style={[
            styles.rowValueContainer,
            isSmall && styles.rowValueContainerSmall,
          ]}
        >
          {children}
        </View>
      </View>
      {!isLast && <Separator />}
    </>
  );
};

const BinaryPicker = observer(
  ({
    coach,
    screenSize,
    firestoreProperty,
    option1Text,
    option2Text,
    getValue = (index) => index === 0,
    disabled = false,
    valueOverride,
  }) => {
    const option1Value = getValue(0);
    const option2Value = getValue(1);
    const currentValue = valueOverride ?? coach.coachSettings[firestoreProperty];
    const isFirstSelected = currentValue === option1Value;

    return (
      <ProfileSegmentedControl
        options={[option1Text, option2Text]}
        value={isFirstSelected}
        width={screenSize === "small" ? "100%" : 260}
        disabled={disabled}
        onChange={(selectedFirstOption) => {
          if (!coach.isLoading && !disabled) {
            coach.setSettings(
              firestoreProperty,
              selectedFirstOption ? option1Value : option2Value,
            );
          }
        }}
      />
    );
  },
);

const LegacyViewPicker = ({
  isUsingLegacyPlanView,
  screenSize,
  onEnableLegacyView,
  onDisableLegacyView,
}) => (
  <ProfileSegmentedControl
    options={[
      "legacyBlockViewOptionLegacy-string",
      "legacyBlockViewOptionNew-string",
    ]}
    value={isUsingLegacyPlanView}
    width={screenSize === "small" ? "100%" : 260}
    onChange={(selectedLegacy) => {
      if (selectedLegacy) {
        onEnableLegacyView();
      } else {
        onDisableLegacyView();
      }
    }}
  />
);

const TextName = observer(
  ({ coach, screenSize, firestoreProperty, placeholder, t }) => (
    <View
      style={[
        styles.textInputContainer,
        screenSize === "small" && styles.inputFullWidth,
      ]}
    >
      <TextInput
        value={coach.coachSettings[firestoreProperty] || ""}
        onChangeText={(text) => coach.setSettings(firestoreProperty, text)}
        style={[styles.textInputStyle, Fonts.regular14]}
        placeholder={t(placeholder)}
        placeholderTextColor={Colors.grayColor}
        maxLength={40}
      />
    </View>
  ),
);

const UnmutableTextName = ({ coach, screenSize, property, placeholder }) => (
  <View
    style={[
      styles.textInputContainer,
      styles.readOnlyInput,
      screenSize === "small" && styles.inputFullWidth,
    ]}
  >
    <Text
      style={[styles.textInputStyle, Fonts.regular14, styles.readOnlyText]}
      numberOfLines={1}
    >
      {coach.getProperty(property) || placeholder}
    </Text>
  </View>
);

const CoachSettings = observer(
  ({ coach, registerUnsavedLeaveHandlers = null }) => {
    const auth = getAuth();
    const { t, i18n } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const settingsLogoMaxWidth = getHeaderLogoMaxWidth(screenSize);
    const shouldShowNutritionDefaults = shouldShowNutritionFromSidebarConfig({
      coach,
      sidebarConfig: coach?.sidebarConfig,
    });
    const shouldShowBlockDashboardSection =
      coach?.coachSettings?.show_block_dashboard_section ??
      coach?.shouldShowBlockDashboardSection ??
      true;

    const [showingResetExercises, setShowingResetExercises] = useState(false);
    const [showingResetAttributes, setShowingResetAttributes] = useState(false);
    const [showingDeleteAccountTypedModal, setShowingDeleteAccountTypedModal] =
      useState(false);
    const [typedDeleteAccountEmail, setTypedDeleteAccountEmail] = useState("");
    const [showingDeleteLogoWarning, setShowingDeleteLogoWarning] =
      useState(false);
    const [showingUploadImageError, setShowingUploadImageError] =
      useState(false);
    const [showingLegacyViewModal, setShowingLegacyViewModal] = useState(false);
    const [uploadImageErrorText, setUploadImageErrorText] = useState(
      "uploadFailedText-string",
    );

    useEffect(() => {
      syncCoachSettingsDraftWithPersistedData(coach);
    }, [coach]);

    const isUsingLegacyPlanView = Boolean(coach?.useLegacyPlanView);
    const shouldShowLegacyBlockViewSettings = Boolean(
      coach?.isLegacyCoachForBlockView?.(),
    );

    const openURL = async (url) => {
      try {
        await Linking.openURL(url);
      } catch (error) {
        console.error(`Failed to open URL: ${url}`, error);
      }
    };

    const getDocsURL = () => {
      const languagePrefix = i18n.language === "es" ? "/es" : "";
      return `https://app.efortcoach.com/docs${languagePrefix}/program/create-block`;
    };

    const handleEnableLegacyView = () => {
      setShowingLegacyViewModal(true);
    };

    const handleConfirmEnableLegacyView = async () => {
      if (coach) {
        await coach.setUseLegacyPlanView(true);
      }
      setShowingLegacyViewModal(false);
    };

    const handleDisableLegacyView = async () => {
      if (coach) {
        await coach.setUseLegacyPlanView(false);
      }
    };

    const deleteAllClientsAndCoach = async () => {
      const coachClients = coach.clients;

      try {
        for (const client of coachClients) {
          await client.deleteClient();
        }

        const coachDocRef = doc(db, "coaches", coach.id);
        await deleteDoc(coachDocRef);
        await signOut(auth);
      } catch (error) {
        console.error("Error during the deletion process:", error);
      }
    };

    const closeDeleteAccountTypedModal = () => {
      setShowingDeleteAccountTypedModal(false);
      setTypedDeleteAccountEmail("");
    };

    const openDeleteAccountTypedModal = () => {
      setTypedDeleteAccountEmail("");
      setShowingDeleteAccountTypedModal(true);
    };

    const isDeleteAccountConfirmEnabled = matchesTypedConfirmationValue({
      input: typedDeleteAccountEmail,
      expectedValue: coach?.email,
    });

    const confirmDeleteAccount = async () => {
      if (!isDeleteAccountConfirmEnabled) return;
      await deleteAllClientsAndCoach();
      closeDeleteAccountTypedModal();
    };

    const logout = () => {
      logoutCoachSession({ auth, coach });
    };

    const saveCoachProfile = async () => {
      if (coach.isOffline) return;

      let hasChanges = false;
      let hasToUpdateApplicationForm = false;

      const settingsChecks = [
        { key: "coach_language", prop: "coachLanguage", updateForm: true },
        { key: "name", prop: "name", updateForm: true },
        { key: "surname", prop: "surname", updateForm: true },
        { key: "exercise_unit", prop: "exerciseUnit" },
        { key: "rir_or_rpe", prop: "rirOrRpe" },
        {
          key: "show_athlete_exercise_stats",
          prop: "showAthleteExerciseStats",
          defaultValue: false,
        },
        {
          key: "show_set_priority",
          prop: "showSetPriority",
          defaultValue: true,
        },
        {
          key: "default_block_view",
          prop: "defaultBlockView",
          defaultValue: "workouts-string",
        },
        {
          key: "show_block_dashboard_section",
          prop: "showBlockDashboardSection",
          defaultValue: true,
        },
        {
          key: "default_show_nutrition_plan_totals",
          prop: "defaultShowNutritionPlanTotals",
          defaultValue: true,
        },
      ];

      settingsChecks.forEach(({ key, prop, updateForm, defaultValue }) => {
        const currentValue =
          defaultValue !== undefined
            ? (coach[prop] ?? defaultValue)
            : coach[prop];

        if (coach.coachSettings[key] !== currentValue) {
          if (key === "coach_language") {
            coach.translate(coach.coachSettings[key], t);
          }
          coach.setProperty(prop, coach.coachSettings[key]);
          hasChanges = true;
          if (updateForm) hasToUpdateApplicationForm = true;
        }
      });

      if (hasChanges) {
        const {
          show_athlete_exercise_stats,
          show_set_priority,
          default_block_view,
          show_block_dashboard_section,
          default_show_nutrition_plan_totals,
          ...settingsToSave
        } = coach.coachSettings;

        coach.saveCoachProfile(settingsToSave);

        try {
          const publicCoachData = {
            id: coach.id,
            name: coach.coachSettings["name"],
            surname: coach.coachSettings["surname"],
            email: coach.email,
            rir_or_rpe: coach.coachSettings["rir_or_rpe"],
            exercise_unit: coach.coachSettings["exercise_unit"],
            show_set_priority:
              coach.coachSettings["show_set_priority"] ?? false,
            show_athlete_exercise_stats:
              coach.coachSettings["show_athlete_exercise_stats"] ?? false,
            default_block_view:
              coach.coachSettings["default_block_view"] ?? "workouts-string",
            show_block_dashboard_section:
              coach.coachSettings["show_block_dashboard_section"] ?? true,
            default_show_nutrition_plan_totals:
              coach.coachSettings["default_show_nutrition_plan_totals"] ?? true,
            language: coach.coachSettings["coach_language"],
          };

          await coach.updateCoachPublicProfile(publicCoachData);
        } catch (error) {
          console.error("Error updating public coach data:", error);
        }
      }

      if (hasToUpdateApplicationForm) {
        try {
          const coachingFormDocRef = doc(
            db,
            "coaching_application_forms",
            coach.id,
          );
          const docSnapshot = await getDoc(coachingFormDocRef);

          if (docSnapshot.exists()) {
            await updateDoc(coachingFormDocRef, {
              coach_language: coach.coachLanguage,
              coach_name: coach.name,
              coach_surname: coach.surname,
            });
          }
        } catch (error) {
          console.error("Error updating coaching form:", error);
        }
      }
    };
    useEffect(() => {
      if (!registerUnsavedLeaveHandlers) {
        return undefined;
      }

      registerUnsavedLeaveHandlers({
        discard: () => {
          discardCoachSettingsDraft(coach);
        },
      });

      return () => registerUnsavedLeaveHandlers(null);
    }, [coach, registerUnsavedLeaveHandlers]);

    const resetPredefinedExercises = async () => {
      const filteredCustomExercises = coach.customPredefinedExercises.filter(
        (exercise) => !exercise.name.includes("-string"),
      );
      const predefinedExercises = coach.getPredefinedArray();

      coach.customPredefinedExercises = [
        ...filteredCustomExercises,
        ...predefinedExercises,
      ];

      const exercisesArray = coach.customPredefinedExercises.map(
        (exercise, index) => ({
          id: exercise.id.toString().toLowerCase(),
          name: exercise.name,
          notes: exercise.notes,
          order: index,
          original_name: exercise.originalName,
          type: exercise.type,
          link: exercise.link,
          is_predefined: true,
        }),
      );

      const userCoachRef = doc(db, "coaches", coach.id);
      try {
        await updateDoc(userCoachRef, {
          custom_predefined_exercises: exercisesArray,
        });
      } catch (error) {
        console.error("Error updating custom predefined exercises:", error);
      }
    };

    const resetPredefinedAttributes = async () => {
      const filteredCustomAttributes = coach.customPredefinedAttributes.filter(
        (attribute) => !attribute.name.includes("-string"),
      );
      const predefinedAttributes = coach.getPredefinedAttributes();

      coach.customPredefinedAttributes = [
        ...filteredCustomAttributes,
        ...predefinedAttributes,
      ];

      const attributesArray = coach.customPredefinedAttributes.map(
        (attribute, index) => ({
          id: attribute.id.toString().toLowerCase(),
          name: attribute.name,
          title: attribute.title,
          subtitle: attribute.subtitle,
          order: index,
          is_predefined: true,
        }),
      );

      const userCoachRef = doc(db, "coaches", coach.id);
      try {
        await updateDoc(userCoachRef, {
          custom_predefined_attributes: attributesArray,
        });
      } catch (error) {
        console.error("Error updating custom predefined attributes:", error);
      }
    };

    const deleteLogo = async () => {
      try {
        const storageRef = ref(storage, `coach_logos/${coach.id}`);
        await deleteObject(storageRef);

        coach.setProperty("logoUrl", null);
        coach.setProperty("logoAspectRatioWH", null);
        coach.setProperty("logoAspectRatioHW", null);
      } catch (error) {
        console.error("Error deleting logo:", error);
      }
    };

    const triggerLogoPicker = () => {
      if (coach.isOffline) return;
      document.getElementById(FILE_INPUT_ID)?.click();
    };

    const ActionButton = ({ onPress, label, disabled = false }) => (
      <HoverableTouchableOpacity
        onPress={onPress}
        style={[styles.primaryActionButton, disabled && styles.disabledOpacity]}
        hoverStyle={styles.primaryActionButtonHover}
        disabled={disabled || coach.isOffline}
      >
        <Text style={[Fonts.medium14, styles.primaryActionButtonText]}>
          {t(label)}
        </Text>
      </HoverableTouchableOpacity>
    );

    const ImageUploadComponent = observer(() => {
      const handleUpload = async (selectedImage) => {
        if (!selectedImage || !coach.id) return;

        try {
          const storageRef = ref(storage, `coach_logos/${coach.id}`);
          await uploadBytes(storageRef, selectedImage);
          const downloadURL = await getDownloadURL(storageRef);

          coach.setProperty("isLogoUploading", false);
          coach.setProperty("logoUrl", downloadURL);
        } catch (error) {
          console.error("Error during image upload:", error);
          setUploadImageErrorText("uploadFailedText-string");
          setShowingUploadImageError(true);
          coach.setProperty("isLogoUploading", false);
        }
      };

      const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
          coach.setProperty("isLogoUploading", true);
          const selectedImage = event.target.files[0];

          if (!selectedImage.type.startsWith("image/")) {
            setUploadImageErrorText("selectImageFileText-string");
            setShowingUploadImageError(true);
            coach.setProperty("isLogoUploading", false);
            return;
          }

          if (selectedImage.size > MAX_SIZE_IN_BYTES) {
            setUploadImageErrorText("imageSizeLimitText-string");
            setShowingUploadImageError(true);
            coach.setProperty("isLogoUploading", false);
            return;
          }

          const imageUrl = URL.createObjectURL(selectedImage);
          const image = new window.Image();
          image.src = imageUrl;
          image.onload = () => {
            coach.setProperty("logoAspectRatioWH", image.width / image.height);
            coach.setProperty("logoAspectRatioHW", image.height / image.width);
            URL.revokeObjectURL(imageUrl);
          };

          handleUpload(selectedImage);
        }
      };

      return (
        <View style={styles.logoContainer}>
          {coach.isLogoUploading ? (
            <View style={styles.logoLoadingContainer}>
              <CustomActivityIndicator large={false} white={false} />
            </View>
          ) : coach.logoUrl ? (
            <View style={styles.logoPreviewWrapper}>
              <LogoImage coach={coach} maxWidth={settingsLogoMaxWidth} />
              <View style={styles.logoActionsRow}>
                <HoverableTouchableOpacity
                  onPress={triggerLogoPicker}
                  style={styles.logoActionButton}
                  hoverStyle={styles.logoActionButtonHover}
                  disabled={coach.isOffline}
                >
                  <Text style={[Fonts.medium13, styles.logoActionText]}>
                    {t("change-string")}
                  </Text>
                </HoverableTouchableOpacity>

                <HoverableTouchableOpacity
                  onPress={() =>
                    !coach.isOffline && setShowingDeleteLogoWarning(true)
                  }
                  style={styles.logoActionButton}
                  hoverStyle={styles.logoDeleteActionButtonHover}
                  disabled={coach.isOffline}
                >
                  <Text style={[Fonts.medium13, styles.logoDeleteText]}>
                    {t("delete-string")}
                  </Text>
                </HoverableTouchableOpacity>
              </View>
            </View>
          ) : (
            <HoverableTouchableOpacity
              onPress={triggerLogoPicker}
              style={[
                styles.logoDropzone,
                coach.isOffline && styles.disabledOpacity,
              ]}
              hoverStyle={styles.logoDropzoneHover}
              disabled={coach.isOffline}
            >
              <Text style={[Fonts.medium13, styles.logoDropzoneText]}>
                {t("uploadYourLogo-string")}
              </Text>
            </HoverableTouchableOpacity>
          )}

          <input
            type="file"
            id={FILE_INPUT_ID}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
        </View>
      );
    });

    const LogOutButton = () => (
      <HoverableTouchableOpacity
        onPress={logout}
        style={[
          styles.secondaryActionButton,
          coach.isOffline && styles.disabledOpacity,
        ]}
        hoverStyle={styles.secondaryActionButtonHover}
        disabled={coach.isOffline}
      >
        <View style={styles.secondaryActionContent}>
          <Feather name="log-out" size={18} color={Colors.blackColor} />
          <Text style={[Fonts.medium14, styles.secondaryActionText]}>
            {t("logOut-string")}
          </Text>
        </View>
      </HoverableTouchableOpacity>
    );

    const DeleteAccountButton = () => (
      <HoverableTouchableOpacity
        onPress={openDeleteAccountTypedModal}
        style={[
          styles.deleteAccountButton,
          coach.isOffline && styles.disabledOpacity,
        ]}
        hoverStyle={styles.deleteAccountButtonHover}
        disabled={coach.isOffline}
      >
        <Text style={[Fonts.medium14, styles.deleteAccountText]}>
          {t("deleteAccount-string")}
        </Text>
      </HoverableTouchableOpacity>
    );

    const SaveButton = observer(() => {
      const hasChanges = hasCoachSettingsUnsavedChanges(coach);

      return (
        <HoverableTouchableOpacity
          onPress={saveCoachProfile}
          style={[
            styles.saveButton,
            (!hasChanges || coach.isOffline) && styles.disabledOpacity,
          ]}
          hoverStyle={styles.saveButtonHover}
          disabled={!hasChanges || coach.isOffline}
        >
          {coach.isLoading ? (
            <CustomActivityIndicator large={false} white={true} />
          ) : (
            <Text style={[Fonts.medium14, styles.saveButtonText]}>
              {t("save-string")}
            </Text>
          )}
        </HoverableTouchableOpacity>
      );
    });

    return (
      <View style={[styles.container, { marginBottom: 20 }]}>
        <View>
          <View
            style={[
              styles.headerRow,
              { marginTop: screenSize === "small" ? 1 : 5 },
            ]}
          >
            <Text style={[Fonts.bold24, styles.title]}>
              {t("settings-string")}
            </Text>
            <SaveButton />
          </View>

          {coach && (
            <>
              <Section
                title={t("profile-string")}
                topMargin={false}
                screenSize={screenSize}
              >
                <SettingsRow label={t("name-string")} screenSize={screenSize}>
                  <View
                    style={[
                      styles.nameColumn,
                      screenSize === "small" && styles.inputFullWidth,
                    ]}
                  >
                    <TextName
                      coach={coach}
                      screenSize={screenSize}
                      firestoreProperty="name"
                      placeholder="name-string"
                      t={t}
                    />
                    <View style={styles.nameInputSpacer} />
                    <TextName
                      coach={coach}
                      screenSize={screenSize}
                      firestoreProperty="surname"
                      placeholder="surname-string"
                      t={t}
                    />
                  </View>
                </SettingsRow>

                <SettingsRow label={t("email-string")} screenSize={screenSize}>
                  <UnmutableTextName
                    coach={coach}
                    screenSize={screenSize}
                    property="email"
                    placeholder="entrenador@gmail.com"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("yourLogo-string")}
                  screenSize={screenSize}
                  isLast
                >
                  <ImageUploadComponent />
                </SettingsRow>
              </Section>

              <Section title={t("preferences-string")} screenSize={screenSize}>
                <SettingsRow
                  label={t("onboardingPreferenceMeasurementUnit-string")}
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="exercise_unit"
                    option1Text="onboardingPreferenceKg-string"
                    option2Text="onboardingPreferenceLbs-string"
                    getValue={(index) => (index === 0 ? false : true)}
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("onboardingPreferenceIntensityRating-string")}
                  tooltip="onboardingPreferenceIntensityRatingTooltip-string"
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="rir_or_rpe"
                    option1Text="onboardingPreferenceRPE-string"
                    option2Text="onboardingPreferenceRIR-string"
                    getValue={(index) => (index === 0 ? false : true)}
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("onboardingPreferenceShowExerciseStats-string")}
                  tooltip="onboardingPreferenceShowExerciseStatsTooltip-string"
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="show_athlete_exercise_stats"
                    option1Text="onboardingPreferenceShow-string"
                    option2Text="onboardingPreferenceHide-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("onboardingPreferenceShowPriority-string")}
                  tooltip="onboardingPreferenceShowPriorityTooltip-string"
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="show_set_priority"
                    option1Text="onboardingPreferenceShow-string"
                    option2Text="onboardingPreferenceHide-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("showBlockDashboardSection-string")}
                  tooltip="showBlockDashboardSectionTooltip-string"
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="show_block_dashboard_section"
                    option1Text="onboardingPreferenceShow-string"
                    option2Text="onboardingPreferenceHide-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("preferenceDefaultBlockView-string")}
                  tooltip="preferenceDefaultBlockViewTooltip-string"
                  screenSize={screenSize}
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="default_block_view"
                    option1Text="workouts-string"
                    option2Text="dashboard-string"
                    getValue={(index) =>
                      index === 0 ? "workouts-string" : "dashboard-string"
                    }
                    disabled={!shouldShowBlockDashboardSection}
                    valueOverride={
                      !shouldShowBlockDashboardSection
                        ? "workouts-string"
                        : undefined
                    }
                  />
                </SettingsRow>

                {shouldShowNutritionDefaults ? (
                  <SettingsRow
                    label={t(
                      "nutritionPlanTotalsDefaultForNewClients-string",
                      "Show calories and macros by default"
                    )}
                    tooltip="nutritionPlanTotalsDefaultForNewClientsTooltip-string"
                    screenSize={screenSize}
                  >
                    <BinaryPicker
                      coach={coach}
                      screenSize={screenSize}
                      firestoreProperty="default_show_nutrition_plan_totals"
                      option1Text="onboardingPreferenceShow-string"
                      option2Text="onboardingPreferenceHide-string"
                    />
                  </SettingsRow>
                ) : null}

                {shouldShowLegacyBlockViewSettings && (
                  <SettingsRow
                    label={t("legacyBlockViewSettingsTitle-string")}
                    screenSize={screenSize}
                  >
                    <LegacyViewPicker
                      isUsingLegacyPlanView={isUsingLegacyPlanView}
                      screenSize={screenSize}
                      onEnableLegacyView={handleEnableLegacyView}
                      onDisableLegacyView={handleDisableLegacyView}
                    />
                  </SettingsRow>
                )}

                <SettingsRow
                  label={t("language-string")}
                  screenSize={screenSize}
                  isLast
                >
                  <BinaryPicker
                    coach={coach}
                    screenSize={screenSize}
                    firestoreProperty="coach_language"
                    option1Text="ES"
                    option2Text="EN"
                    getValue={(index) => (index === 0 ? "es" : "en")}
                  />
                </SettingsRow>
              </Section>

              <View style={[styles.settingsCard, styles.inlineCardSpacing]}>
                <SettingsRow
                  label={t("resetPredefinedExercises-string")}
                  screenSize={screenSize}
                >
                  <ActionButton
                    onPress={() => setShowingResetExercises(true)}
                    label="resetButton-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("resetPredefinedAttributes-string")}
                  screenSize={screenSize}
                  isLast
                >
                  <ActionButton
                    onPress={() => setShowingResetAttributes(true)}
                    label="resetButton-string"
                  />
                </SettingsRow>
              </View>

              <Section title={t("learnAndUpdates-string")} screenSize={screenSize}>
                <SettingsRow
                  label={t("tutorials-string")}
                  screenSize={screenSize}
                  hasDescription
                  description={t("learnToUseEfortCoach-string")}
                  noTranslateDescription
                >
                  <ActionButton
                    onPress={() => openURL(getDocsURL())}
                    label="viewTutorials-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("whatsNew-string")}
                  screenSize={screenSize}
                  hasDescription
                  description={t("currentVersion-string", {
                    version: getFormattedVersion(),
                  })}
                  noTranslateDescription
                  isLast
                >
                  <ActionButton
                    onPress={() => coach.setProperty("showingWhatsNew", true)}
                    label="viewWhatsNew-string"
                  />
                </SettingsRow>
              </Section>

              <Section title={t("support-string")} screenSize={screenSize}>
                <SettingsRow
                  label={t("reportProblem-string")}
                  screenSize={screenSize}
                  hasDescription
                  description={t("reportProblemText-string")}
                >
                  <ActionButton
                    onPress={() => {
                      if (coach.email == "efortapp@gmail.com") {
                        coach.clientsDemoData("B2Xm8CFPyIS2taVlusbcIicWItF3");
                      } else {
                        openURL(INSTAGRAM_URL);
                      }
                    }}
                    label="contactUs-string"
                  />
                </SettingsRow>

                <SettingsRow
                  label={t("sendFeedbackSettings-string")}
                  screenSize={screenSize}
                  isLast
                >
                  <ActionButton
                    onPress={() => {
                      if (coach.email == "efortapp@gmail.com") {
                        coach.clientsDemoData(
                          "B2Xm8CFPyIS2taVlusbcIicWItF3",
                          false,
                        );
                      } else {
                        coach.setProperty("showingSendFeedback", true);
                      }
                    }}
                    label="sendFeedbackSettings-string"
                  />
                </SettingsRow>
              </Section>

              {coach.subscriptionStatus === "active" &&
                !coach.allowedAccount && (
                  <Section
                    title={t("subscription-string")}
                    screenSize={screenSize}
                  >
                    <SettingsRow
                      label={t("manageSubscription-string")}
                      screenSize={screenSize}
                      isLast
                    >
                      <ActionButton
                        onPress={() => openURL(STRIPE_BILLING_URL)}
                        label="manage-string"
                      />
                    </SettingsRow>

                    {/* {coach.clients.length === coach.maxClients && (
                <Text style={[Fonts.regular13, styles.subscriptionHintText]}>
                  {t("useUpgradeDiscount-string")}
                </Text>
              )} */}
                  </Section>
                )}

              <View style={styles.accountSectionHeader}>
                <Text style={[Fonts.medium20, styles.sectionTitle]}>
                  {t("account-string")}
                </Text>
              </View>

              <View style={styles.accountActionsRow}>
                <LogOutButton />
              </View>

              <DeleteAccountButton />

              <BlockViewRevertModal
                isVisible={showingLegacyViewModal}
                onClose={() => setShowingLegacyViewModal(false)}
                onRevert={handleConfirmEnableLegacyView}
                coach={coach}
              />

              <CustomAlert
                isVisible={showingResetExercises}
                onClose={() => setShowingResetExercises(false)}
                title={ALERT_CONFIGS.resetExercises.title}
                text={ALERT_CONFIGS.resetExercises.text}
                action={resetPredefinedExercises}
                specialCTA={ALERT_CONFIGS.resetExercises.cta}
              />
              <CustomAlert
                isVisible={showingResetAttributes}
                onClose={() => setShowingResetAttributes(false)}
                title={ALERT_CONFIGS.resetAttributes.title}
                text={ALERT_CONFIGS.resetAttributes.text}
                action={resetPredefinedAttributes}
                specialCTA={ALERT_CONFIGS.resetAttributes.cta}
              />
              <TypedConfirmationModal
                isVisible={showingDeleteAccountTypedModal}
                onClose={closeDeleteAccountTypedModal}
                title={t(ALERT_CONFIGS.deleteAccount.title)}
                description={t(ALERT_CONFIGS.deleteAccount.text)}
                instruction={t("coachDashboardTypeToConfirmDelete-string", {
                  expectedValue: coach?.email || "",
                })}
                placeholder={t(
                  "coachSettingsDeleteAccountEmailPlaceholder-string",
                )}
                value={typedDeleteAccountEmail}
                onChangeText={setTypedDeleteAccountEmail}
                onConfirm={confirmDeleteAccount}
                isConfirmEnabled={isDeleteAccountConfirmEnabled}
                confirmLabel={t("delete-string")}
                cancelLabel={t("cancel-string")}
              />
              <CustomAlert
                isVisible={showingUploadImageError}
                onClose={() => setShowingUploadImageError(false)}
                title={ALERT_CONFIGS.uploadError.title}
                text={uploadImageErrorText}
                specialCTA={ALERT_CONFIGS.uploadError.cta}
              />
              <CustomAlert
                isVisible={showingDeleteLogoWarning}
                onClose={() => setShowingDeleteLogoWarning(false)}
                title={ALERT_CONFIGS.deleteLogo.title}
                text={ALERT_CONFIGS.deleteLogo.text}
                action={deleteLogo}
                specialCTA={ALERT_CONFIGS.deleteLogo.cta}
              />
            </>
          )}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: {
    fontWeight: "600",
  },
  sectionTitle: {
    marginBottom: 12,
    fontWeight: "500",
  },
  settingsCard: {
    backgroundColor: Colors.whiteColor,
    padding: 24,
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
  },
  inlineCardSpacing: {
    marginTop: 28,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rowSmall: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  rowLabelContainer: {
    width: 250,
    paddingRight: 20,
  },
  rowLabelContainerSmall: {
    width: "100%",
    paddingRight: 0,
    marginBottom: 12,
  },
  rowLabelLine: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowLabel: {
    color: Colors.blackColor,
    flexShrink: 1,
  },
  rowDescription: {
    color: Colors.grayColor,
    marginTop: 8,
    lineHeight: 18,
  },
  rowValueContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  rowValueContainerSmall: {
    width: "100%",
    alignItems: "center",
  },
  separator: {
    height: 1,
    backgroundColor: Colors.lightGrayColor,
    width: "100%",
    marginVertical: 20,
  },
  tooltipIconContainer: {
    marginLeft: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  textInputContainer: {
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 40,
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    width: "45%",
    minWidth: 180,
  },
  inputFullWidth: {
    width: "100%",
    minWidth: 0,
  },
  textInputStyle: {
    height: 40,
    color: Colors.blackColor,
    outlineStyle: "none",
    width: "100%",
  },
  readOnlyInput: {
    borderWidth: 0,
    paddingHorizontal: 0,
    minWidth: 0,
  },
  readOnlyText: {
    color: Colors.grayColor,
    lineHeight: 40,
    textAlign: "center",
  },
  nameColumn: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  nameInputSpacer: {
    marginVertical: 6,
  },
  primaryActionButton: {
    height: 36,
    borderRadius: 14,
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryActionButtonHover: {
    backgroundColor: Colors.darkBubbleColor,
  },
  primaryActionButtonText: {
    color: Colors.whiteColor,
  },
  saveButton: {
    height: 40,
    minWidth: 100,
    borderRadius: 14,
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  saveButtonHover: {
    backgroundColor: Colors.darkBubbleColor,
  },
  saveButtonText: {
    color: Colors.whiteColor,
  },
  disabledOpacity: {
    opacity: 0.5,
  },
  logoContainer: {
    alignItems: "center",
  },
  logoLoadingContainer: {
    height: 50,
    justifyContent: "center",
    alignItems: "center",
  },
  logoPreviewWrapper: {
    alignItems: "center",
  },
  logoActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
    gap: 8,
  },
  logoActionButton: {
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  logoActionButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  logoDeleteActionButtonHover: {
    backgroundColor: "rgba(240, 68, 56, 0.1)",
  },
  logoActionText: {
    color: Colors.grayColor,
  },
  logoDeleteText: {
    color: Colors.redColor,
  },
  logoDropzone: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderStyle: "dashed",
    backgroundColor: Colors.whiteColor,
    paddingVertical: 14,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
    minWidth: 180,
  },
  logoDropzoneHover: {
    backgroundColor: Colors.hoveredGray,
  },
  logoDropzoneText: {
    color: Colors.grayColor,
    textAlign: "center",
  },
  secondaryActionButton: {
    height: 40,
    paddingHorizontal: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    justifyContent: "center",
  },
  secondaryActionButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  secondaryActionContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  secondaryActionText: {
    marginLeft: 6,
    color: Colors.blackColor,
  },
  subscriptionHintText: {
    marginTop: 18,
    color: Colors.grayColor,
  },
  accountSectionHeader: {
    marginTop: 28,
  },
  accountActionsRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  deleteAccountButton: {
    marginTop: 24,
    marginBottom: 20,
    alignSelf: "flex-start",
    height: 40,
    justifyContent: "center",
    backgroundColor: "transparent",
    borderWidth: 0,
    borderRadius: 14,
    paddingVertical: 0,
    paddingHorizontal: 12,
  },
  deleteAccountButtonHover: {
    backgroundColor: "rgba(240, 68, 56, 0.08)",
  },
  deleteAccountText: {
    color: Colors.redColor,
  },
});

export default CoachSettings;
